import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_PAGINATION_PAGE } from '@shared/constants/pagination.constants';

import { getDashboardSegmentQueryIdentity } from '../helpers/dashboardScope';
import type { useDashboardSegmentData } from './useDashboardSegmentData';
import type { EDashboardSegmentKey } from '../types/dashboard.types';

type TSegmentReadyRequest = {
  id: number;
  queryIdentity: string;
  hasMatchedQuery: boolean;
};

type TOptions = {
  selectedRootIds: string[];
  selectedSegmentKey: EDashboardSegmentKey;
  page: number;
  pageSize: number;
  segmentData: ReturnType<typeof useDashboardSegmentData>;
  onSelectedSegmentKeyChange: (segmentKey: EDashboardSegmentKey) => void;
};

export const useScrollToSegmentWhenReady = ({
  selectedRootIds,
  selectedSegmentKey,
  page,
  pageSize,
  segmentData,
  onSelectedSegmentKeyChange,
}: TOptions) => {
  const itemSegmentsRef = useRef<HTMLDivElement | null>(null);
  const nextRequestIdRef = useRef(0);
  const [readyRequest, setReadyRequest] = useState<TSegmentReadyRequest>();
  const selectedQueryIdentity = useMemo(
    () =>
      getDashboardSegmentQueryIdentity({
        selectedRootIds,
        segmentKey: selectedSegmentKey,
        page,
        pageSize,
      }),
    [page, pageSize, selectedRootIds, selectedSegmentKey]
  );

  const scrollToItemSegments = useCallback(() => {
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    itemSegmentsRef.current?.scrollIntoView({ behavior, block: 'start' });
  }, []);

  const onMetricSegmentChange = useCallback(
    (segmentKey: EDashboardSegmentKey) => {
      setReadyRequest({
        id: ++nextRequestIdRef.current,
        queryIdentity: getDashboardSegmentQueryIdentity({
          selectedRootIds,
          segmentKey,
          page: DEFAULT_PAGINATION_PAGE,
          pageSize,
        }),
        hasMatchedQuery: false,
      });
      onSelectedSegmentKeyChange(segmentKey);
      itemSegmentsRef.current?.focus({ preventScroll: true });
      scrollToItemSegments();
    },
    [onSelectedSegmentKeyChange, pageSize, scrollToItemSegments, selectedRootIds]
  );

  const onSegmentTabChange = useCallback(
    (segmentKey: EDashboardSegmentKey) => {
      setReadyRequest(undefined);
      onSelectedSegmentKeyChange(segmentKey);
    },
    [onSelectedSegmentKeyChange]
  );

  useEffect(() => {
    setReadyRequest((currentRequest) => {
      if (!currentRequest) return currentRequest;
      const matchesSelectedQuery = currentRequest.queryIdentity === selectedQueryIdentity;
      if (matchesSelectedQuery && !currentRequest.hasMatchedQuery) return { ...currentRequest, hasMatchedQuery: true };
      if (!matchesSelectedQuery && currentRequest.hasMatchedQuery) return undefined;
      return currentRequest;
    });
  }, [readyRequest?.id, selectedQueryIdentity]);

  useLayoutEffect(() => {
    if (
      !readyRequest ||
      readyRequest.queryIdentity !== selectedQueryIdentity ||
      segmentData.isPlaceholderData ||
      segmentData.isFetching
    )
      return;

    if (segmentData.segmentPage) {
      setReadyRequest(undefined);
      scrollToItemSegments();
    } else if (segmentData.error) {
      setReadyRequest(undefined);
    }
  }, [
    readyRequest,
    scrollToItemSegments,
    segmentData.error,
    segmentData.isFetching,
    segmentData.isPlaceholderData,
    segmentData.segmentPage,
    selectedQueryIdentity,
  ]);

  return {
    itemSegmentsRef,
    onMetricSegmentChange,
    onSegmentTabChange,
  };
};
